import {select} from 'd3-selection';
import * as shortcuts from "../modules/shortcuts";
import * as crossMapLookup from "./crossMapLookup";

const d3 = {select};

const getRangeBases = (fromBase, endBase, sequence) => {
    if (sequence == null || sequence.length === 0) {
        return Array.isArray(sequence) ? [] : '';
    }

    const start = Math.round(fromBase);
    const end = Math.round(endBase);

    return sequence.slice(start, end);
}


const textSize = (text, selector, fontSize = 1) => {
    if (!d3) return;
    const container = d3.select(selector).append('svg');
    container.append('text').attr("x", -99999).attr("y", -99999).style("font-size", fontSize + "em").text(text);
    const size = container.node().getBBox();
    container.remove();
    return {width: size.width, height: size.height};
}

const copyToClipboard = (data, selector) => {
    const $toastContainer = document.querySelector(selector);
    shortcuts.copy($toastContainer, data);
}


const toolTipContent = (residue) => {
    const $modificationResidue = document.createElement('div');
    $modificationResidue.classList.add("list-group", "m-0")
    const $residueChar = document.createElement('div');
    $residueChar.classList.add("list-group-item", "d-flex", "justify-content-between");
    $residueChar.innerHTML = `${residue.abbreviation}`;
    $modificationResidue.appendChild($residueChar)

    if (!residue.bond) {
        const nucleobaseModifications = residue.nitrogenousBaseType;
        const sugarModification = residue.sugarBaseType;

        const $backboneModification = document.createElement('div');
        $backboneModification.classList.add("list-group-item", "d-flex", "justify-content-between")
        $backboneModification.innerHTML = `Base: ${nucleobaseModifications}`;

        const $sugarModification = document.createElement('div');
        $sugarModification.classList.add("list-group-item", "d-flex", "justify-content-between")
        $sugarModification.innerHTML = `Sugar: ${sugarModification}`;

        $modificationResidue.appendChild($backboneModification)
        $modificationResidue.appendChild($sugarModification)
    }
    return $modificationResidue.outerHTML;
}

const crossMapDataEntry = ({pk = 0, symbol, bond = true, sortKey = 0, cartoon = {}, raw = {}, sequence = {}}) => {
    return {
        cartoon: {start: cartoon.start ?? 0, end: cartoon.end ?? 0},
        raw: {start: raw.start ?? 0, end: raw.end ?? 0},
        sequence: {start: sequence.start ?? 0, end: sequence.end ?? 0},
        pk,
        symbol,
        bond,
        sortKey
    };
}

const cartoonDataEntry = ({enc, typeMap, cartoonIndex, rawIndex, charIndex}) => {
    return {
        enc,
        typeMap,
        "bond": typeMap.type === "linkage",
        "shape": typeMap.shape,
        "color": typeMap.color,
        "base": typeMap.symbol,
        "abbreviation": typeMap.abbreviation,
        "nitrogenousBaseType": typeMap.nitrogenous_base_type,
        "sugarBaseType": typeMap.sugar_base_type,
        "cartoonStart": cartoonIndex,
        "rawStart": rawIndex,
        charIndex
    }
};

const rawSequenceIndexDataEntry = ({char, index, start}) => {
    return {
        char,
        index,
        start
    };
}


const slotDataEntry = ({slot, start, end}) => {
    return {
        "pk": slot.pk,
        "name": slot.name,
        "length": slot.length,
        "editable": slot.editable,
        "downstream_linkage": slot.downstream_linkage,
        "dna_sequence": {
            "friendly_id": slot.dna_sequence.friendly_id,
            "encodings": slot.dna_sequence?.encodings ? slot.dna_sequence?.encodings : [],
            "name": slot.dna_sequence.name,
            "description": slot.dna_sequence.description,
            "version_name": slot.dna_sequence.version_name,
            "obsolete": slot.dna_sequence.obsolete,
            "editable": slot.dna_sequence.editable,
            "public": slot.dna_sequence.public,
            "num_collaborators": slot.dna_sequence.num_collaborators,
            "annotations": slot.dna_sequence.annotations,
            "urls": slot.dna_sequence.urls,
        },
        "direction": slot.direction,
        "title": slot.title,
        "size": slot.size,
        "shape": slot.shape,
        "content": slot.content,
        "urls": slot.urls,
        start,
        end
    }
}


const constructRNAEncodedData = (data) => {
    let encodedData = [];
    let encodedSlots = [];
    let start = 0;
    let end = 0;
    data.slots?.forEach(s => {
        const encodings = s.dna_sequence.encodings?.length
            ? s.dna_sequence.encodings
            : [];

        if (s.downstream_linkage) {
            encodings.push({
                "id": -1,
                "sort_key": s.dna_sequence.encodings.length,
                "type": s.downstream_linkage
            })
        }

        end += encodings.length;

        encodedData = [...encodedData, encodings]
        encodedSlots.push(slotDataEntry({
            "slot": s,
            start,
            "end": end
        }))
        start = end
    })

    return {
        encodedData: encodedData.flat(),
        encodedDataSlots: encodedSlots,
    }
}


const decodedRNA = (encodedData, typeMap) => {
    const decodedCartoonMap = [];

    const decodedRawSequenceMapIndexes = []
    let sequence = "";

    const decodedCrossMap = [];

    let cartoonIndex = 0;
    let rawIndex = 0;
    let charIndex = 0;

    const encodingTokens = new Set();

    /**
     * for cross between cartoon/raw and toolbar indexes/sequence positions
     */
    decodedCrossMap.push(crossMapDataEntry({
        cartoon: {start: 0, end: 0},
        raw: {start: 0, end: 0},
        sequence: {start: 0, end: 0},
        bond: true,
        symbol: "|"
    }))

    encodedData?.forEach((enc, index) => {
        const encMap = typeMap[enc.type];
        const rawSymbol = encMap.raw_symbol ?? "";
        let rawSymbolLength = rawSymbol.length;

        sequence += rawSymbol;
        encodingTokens.add(rawSymbol);

        /**
         * indexes for cartoonEditor
         */
        decodedCartoonMap.push(
            cartoonDataEntry({
                enc,
                typeMap: encMap,
                cartoonIndex,
                rawIndex,
                charIndex: charIndex + ((encMap.type === "linkage") ? 0 : 1)  // useful for ruler
            })
        )

        /**
         * indexes for rawEditor
         */
        for (let j = 0; j < rawSymbolLength; j++) {
            let endEncPatternLength = rawIndex + (j > (rawSymbolLength / 2) ? rawSymbolLength : 0);

            decodedRawSequenceMapIndexes.push(rawSequenceIndexDataEntry({
                "char": rawSymbol,
                "index": rawIndex + j,
                "start": endEncPatternLength
            }));
        }

        /**
         * for cross  between cartoon/raw and toolbar indexes/sequence positions
         */
        decodedCrossMap.push(crossMapDataEntry({
            cartoon: {start: cartoonIndex, end: cartoonIndex + 1},
            raw: {start: rawIndex, end: rawIndex + rawSymbolLength},
            sequence: {start: charIndex, end: charIndex + ((encMap.type === "linkage") ? 0 : 1)},
            pk: enc.id,
            symbol: encMap.raw_symbol,
            bond: encMap.type === "linkage",
            sortKey: enc.sort_key
        }))

        decodedCrossMap.push(crossMapDataEntry({
            cartoon: {start: cartoonIndex + 1, end: cartoonIndex + 1},
            raw: {start: rawIndex + rawSymbolLength, end: rawIndex + rawSymbolLength},
            sequence: {
                start: charIndex + ((encMap.type === "linkage") ? 0 : 1),
                end: charIndex + ((encMap.type === "linkage") ? 0 : 1)
            },
            bond: true,
            symbol: "|"
        }))

        cartoonIndex++;
        rawIndex += rawSymbolLength;
        charIndex += ((encMap.type === "linkage") ? 0 : 1)
    });

    return {
        sequence,
        decodedCartoonMap,
        decodedRawSequenceMapIndexes,
        decodedCrossMap,
        encodingTokens,
    }
}

const createVirtualElement = (clientX, clientY) => {
    return {
        getBoundingClientRect: () => ({
            width: 0,
            height: 0,
            top: clientY,
            right: clientX,
            bottom: clientY,
            left: clientX,
        }),
    };
};


const setEditorBoundaries = (offsetScroll, visibleBasesCount, sequenceLength) => {
    if (offsetScroll.current < 0 || (offsetScroll.current + visibleBasesCount) > sequenceLength) {
        if (offsetScroll.current < 1) {
            offsetScroll.current = 0;
        } else {
            offsetScroll.current = sequenceLength - visibleBasesCount;
        }
        return {
            "left": offsetScroll.current,
            "right": offsetScroll.current + visibleBasesCount
        }
    }
    return {
        "left": offsetScroll.current,
        "right": offsetScroll.current + visibleBasesCount
    }
}


const countSelectedBases = (start, end, crossMap, source, paddingEditor) => {
    let startBaseEditor = start - paddingEditor;
    let endBaseEditor = end - paddingEditor;

    // remove '|' bonds
    const characters = crossMap.filter(item => item.symbol !== '|');

    const startBaseEditorIndex = characters.findIndex(item => item[source].start === startBaseEditor);
    const startCharacter = characters[startBaseEditorIndex];

    if (start === end && !startCharacter) {
        const last = characters[characters.length - 1];
        return {
            characters: [],
            bonds: [],
            charIndex: startBaseEditorIndex,
            sequence: {start: last.sequence.end, end: last.sequence.end}
        };
    }

    const endBaseEditorIndex = characters.findIndex(item => item[source].end === endBaseEditor);
    const endCharacter = start === end ? startCharacter : characters[endBaseEditorIndex];

    const pks = getEncodings(crossMap, startBaseEditor, endBaseEditor, source);

    const result = {characters: [], bonds: []};
    for (let i = startBaseEditorIndex; i <= endBaseEditorIndex; i++) {
        const char = characters[i];
        if (char && pks.includes(char.pk)) {
            if (char.bond) result.bonds.push(char.pk);
            else result.characters.push(char.pk);
        }
    }

    return {
        ...result,
        charIndex: startBaseEditorIndex,
        sequence: {
            start: startCharacter.sequence.start,
            end: start === end ? endCharacter.sequence.start : endCharacter.sequence.end
        }
    };
};

/**
 * Returns the pks of the crossMap entries within [start, end].
 * Bond entries with null symbol at the edges are trimmed.
 */
const getEncodings = (crossMap, start, end, source) => {
    let results = crossMap
        .filter(item => {
            const _start = item[source].start;
            const _end = item[source].end;

            if (item.symbol === '|') return false;

            // if bond raw_symbol is null
            if (item.bond && _start === _end) {
                return _start > start && _start <= end;
            }

            if (_start < start || _end > end) return false;
            return !(item.bond && _start === end);
        })
        .map(item => ({
            pk: item.pk,
            sortKey: item.sortKey,
            raw_symbol: item.symbol,
            bond: item.bond
        }));

    // if raw source, remove null edges
    if (source === 'raw') {
        for (let i = 0; i < results.length; i++) {
            if (results[0].bond && results[0].raw_symbol === null) {
                results.shift();
            } else break;
        }
        for (let i = results.length - 1; i >= 0; i--) {
            if (results[results.length - 1].bond && results[results.length - 1].raw_symbol === null) {
                results.pop();
            } else break;
        }
    }

    return results.map(e => e.pk);
}


const findNextItem = (crossMap, currentBasePosition, source, boundary) => {
    const includeDownstreamLinkage = boundary === "end";

    const nextEncoding = crossMapLookup.findEncodingByBoundary(
        crossMap, source, currentBasePosition, "start",
        { includeSeparator: false, includeDownstreamLinkage }
    );

    if (nextEncoding) {
        return { sortKey: nextEncoding.sortKey, pk: nextEncoding.pk };
    }

    // end of sequence: fallback to the last encoding
    const prevEncoding = crossMapLookup.findEncodingByBoundary(
        crossMap, source, currentBasePosition, "end", { includeSeparator: false }
    );

    return prevEncoding
        ? { sortKey: prevEncoding.sortKey + 1, pk: prevEncoding.pk }
        : null;
};

/**
 * Calculates the tick step for a ruler based on sequence length and available width.
 */
function getRulerTickStep(sequenceLength, areaWidth, minSpacingPixel = 40) {
    const sizePerUnit = areaWidth / sequenceLength;
    const rawStep = minSpacingPixel / sizePerUnit;

    // If rawStep < 1, each unit already has enough space → tick every 1
    if (rawStep <= 1) return 1;

    const scale = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const normalized = rawStep / scale;

    let roundedTick;
    if (normalized <= 1) roundedTick = 1;
    else if (normalized <= 2) roundedTick = 2;
    else if (normalized <= 5) roundedTick = 5;
    else roundedTick = 10;

    return roundedTick * scale;
}


const fixedMaxValue = (value, min, max) => Math.max(min, Math.round(Math.min(value, max)));

function getContrastColor(color) {
    let r, g, b;

    if (color.startsWith('#')) {
        const hex = color.replace('#', '');
        const full = hex.length === 3
            ? hex.split('').map(c => c + c).join('')
            : hex;
        r = parseInt(full.slice(0, 2), 16);
        g = parseInt(full.slice(2, 4), 16);
        b = parseInt(full.slice(4, 6), 16);
    } else if (color.startsWith('rgb')) {
        [r, g, b] = color.match(/\d+/g).map(Number);
    }

    const toLinear = c => {
        c /= 255;
        return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };

    const L = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
    return L > 0.179 ? '#000' : '#fff';
}

/**
 * Searches for a contiguous subsequence of tokens within a sequence of encodings.
 * */
const search = (queryString, encodingTokens, sequenceEncodings) => {
    const sortedTokens = [...encodingTokens].sort((a, b) => b.length - a.length);

    // 1. Tokenize queryString
    const queryTokens = [];
    let rawEncodingsLength = 0;

    for (let i = 0; i < queryString.length;) {
        const match = sortedTokens.find(s => queryString.startsWith(s, i));
        if (match) {
            queryTokens.push(match);
            rawEncodingsLength += match.length;
            i += match.length;
        } else {
            return [];
        }
    }

    if (!queryTokens.length) return [];

    const results = [];
    const queryTokensLen = queryTokens.length;

    for (let i = 0; i < sequenceEncodings.length; i++) {
        // Skip null entries as starting point to avoid duplicate matches
        if (sequenceEncodings[i].typeMap.raw_symbol === null) continue;

        let position = i;
        let matched = 0;

        /**
         * Match each token with sorted sequence encodings,
         * skipping null entries, and track the end position
         */
        for (const qt of queryTokens) {
            // Skip raw_symbol null entries
            for (; position < sequenceEncodings.length &&
                   sequenceEncodings[position].typeMap.raw_symbol === null; position++) {
            }

            if (position >= sequenceEncodings.length) break;

            if (sequenceEncodings[position].typeMap.raw_symbol === qt) {
                matched++;
                position++;
            } else {
                break;
            }
        }

        if (matched === queryTokensLen) {
            results.push({
                cartoonStart: sequenceEncodings[i].cartoonStart,
                rawStart: sequenceEncodings[i].rawStart,
                cartoonEnd: sequenceEncodings[i].cartoonStart + (position - i),
                rawEnd: sequenceEncodings[i].rawStart + rawEncodingsLength,
            });
        }
    }

    return results;
}

const getCSSColor = (color) => {
    return getComputedStyle(document.body)
        .getPropertyValue(`--bs-${color}`);
}

const oligoEditorPath = (action, hasSlots, slotPk, friendlyId, pk) => {
    const isEncodingInstance = action === 'update' || action === '';

    let suffix;
    if (action === 'update') {
        suffix = `${pk}/update/`;
    } else if (action === '') {
        suffix = `${pk}/`;          // view
    } else {
        suffix = `${action}/`;      // create, delete, ecc.
    }

    if (hasSlots && slotPk) {
        return `/oligos/slots/${slotPk}/encodings/${suffix}`;
    }

    if (isEncodingInstance) {
        return `/oligos/sequences/encodings/${suffix}`;
    }

    return `/oligos/sequences/${friendlyId}/encodings/${suffix}`;
};

const oligoSlotEditorPath = (baseUrl, action) => {
    if (!baseUrl) return null;
    return `${baseUrl}/${action}/`;
}

const findElementByPk = (encodings, pk) => {
    return encodings.find(item => item.enc.id === pk) ?? null;
};


const getSlotFriendlyIdAtPosition = (currentPosition, slots, source, edge, crossMap = []) => {
    let currentEncoding = crossMapLookup.findEncodingByBoundary(
        crossMap, source, currentPosition, edge, { includeSeparator: false }
    );

    if (!currentEncoding) {
        /*
        fall back to the first real encoding at the  sequence start (pos 0),
        or the last real encoding otherwise (sequence end)
         */
        currentEncoding = crossMapLookup.findEncoding(
            crossMap, source, { last: currentPosition !== 0 }
        );
    }

    const slot = findSlotByEncodingId(slots, currentEncoding?.pk);
    return slot?.dna_sequence?.friendly_id ?? null;
};


const findSlotByFriendlyId = (slots, friendlyId) =>
    slots.find(s => s.dna_sequence?.friendly_id === friendlyId) ?? null;


const findSlotByEncodingId = (slots, encodingId) =>
    slots.find(s => s.dna_sequence?.encodings?.some(enc => enc.id === encodingId)) ?? null;


const canInsertEncoding = (slots, nextEncoding, currentSlotFriendlyID) => {
    if (!currentSlotFriendlyID) return false;
    const slotNextEncoding = findSlotByEncodingId(slots, nextEncoding?.pk);
    return slotNextEncoding?.dna_sequence?.friendly_id === currentSlotFriendlyID;
}

export {
    getRangeBases,
    textSize,
    copyToClipboard,
    toolTipContent,
    constructRNAEncodedData,
    decodedRNA,
    createVirtualElement,
    setEditorBoundaries,
    countSelectedBases,
    getEncodings,
    findNextItem,
    getSlotFriendlyIdAtPosition,
    getRulerTickStep,
    fixedMaxValue,
    getContrastColor,
    search,
    getCSSColor,
    oligoEditorPath,
    oligoSlotEditorPath,
    findElementByPk,
    findSlotByFriendlyId,
    canInsertEncoding
}